import type { ComponentType, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { ContainerWidth, useLayout } from '@/contexts/LayoutContext'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { useScreenWidth } from '@/hooks/useScreenWidth'
import { cn } from '@/lib/utils'
import { useLocation } from 'react-router-dom'

import { ModuleTypeEnum } from '@scrambl/shared'

import { Page } from './Page'
import { PageHeader } from './PageHeader'
import { TabItem } from './PageTabs'

interface PageContainerProps {
  children: ReactNode
  header?: {
    title: string | ReactNode
    titleSuffix?: string
    description?: ReactNode
    greeting?: boolean
    actions?: ReactNode
    children?: ReactNode
    tabs?: TabItem[]
    className?: string
    condensed?: boolean
    fixed?: boolean
    /** Drops the divider under the header (open surfaces like Today). */
    flat?: boolean
    icon?: ComponentType<{ className?: string; weight?: string }> | null
    moduleType?: ModuleTypeEnum
    entityData?: {
      id?: string
      name?: string
      logoUrl?: string
    }
    onEntityClick?: () => void
    onLogoChange?: (file: File | null) => Promise<void>
    backLink?: {
      label: string
      href: string
    }
    /** Callback when a tab view is changed via dropdown (e.g., list/board/timeline) */
    onViewChange?: (tabId: string, viewId: string) => void
    /** Decorative layer rendered absolutely behind the header block */
    background?: ReactNode
    /** Full-width hero content rendered inside the header block, between the title row and the tabs */
    hero?: ReactNode
  }
  className?: string
  isLoading?: boolean
  fullWidth?: boolean
  fullHeight?: boolean
  noHeaderSpacing?: boolean
  noPadding?: boolean
  noBottomPadding?: boolean
  containerWidth?: ContainerWidth
  enableScroll?: boolean
}

export function PageContainer({
  children,
  header,
  className,
  isLoading,
  fullWidth,
  fullHeight,
  noHeaderSpacing,
  noPadding,
  noBottomPadding = false,
  containerWidth,
  enableScroll = true,
}: PageContainerProps) {
  // Get layout options from context
  const { layoutOptions } = useLayout()
  const { isUltrawide, isLargeDesktop, category } = useScreenWidth()

  // Use location to determine if we're on a settings page
  const location = useLocation()
  const { mainNavItems, adminNavItems } = useNavigationItems()
  const isSettingsPage = location.pathname.includes('/settings')

  // Simplified scroll state using intersection observer
  const [isScrolled, setIsScrolled] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Check if this is a discussions page or tab - either standalone or nested in another module
  const isDiscussionsPage = location.pathname.includes('/discussions')
  const isThreadsPage = location.pathname.includes('/threads')
  const currentPathSegment = location.pathname.split('/').pop()
  const isDiscussionsTab = currentPathSegment === 'discussions'
  const isAnyDiscussionsPage =
    isDiscussionsPage || isDiscussionsTab || isThreadsPage

  // Check if this is a workflows page (canvas-style layout)
  const isWorkflowsPage = location.pathname.includes('/workflows')

  // For discussions pages/tabs, threads, and workflows, they handle their own scrolling
  const isCanvasStyleLayout = isAnyDiscussionsPage || isWorkflowsPage
  // Disable scroll for canvas-style layouts - they handle their own scrolling internally
  const effectiveEnableScroll = isCanvasStyleLayout ? false : enableScroll

  // Use intersection observer to detect scroll without layout shift issues
  useEffect(() => {
    if (!effectiveEnableScroll) return

    const sentinel = sentinelRef.current
    const scrollContainer = scrollContainerRef.current

    if (!sentinel || !scrollContainer) return

    // Check if there's enough content to scroll meaningfully
    const checkScrollability = () => {
      const isScrollable =
        scrollContainer.scrollHeight > scrollContainer.clientHeight
      const scrollableAmount =
        scrollContainer.scrollHeight - scrollContainer.clientHeight
      return isScrollable && scrollableAmount > 150 // At least 150px of scrollable content
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!checkScrollability()) {
          setIsScrolled(false)
          return
        }

        // When sentinel is not visible, we've scrolled enough to condense
        setIsScrolled(!entry.isIntersecting)
      },
      {
        root: scrollContainer,
        rootMargin: '0px',
        threshold: 0,
      },
    )

    observer.observe(sentinel)

    // Also check on resize
    const resizeObserver = new ResizeObserver(() => {
      if (!checkScrollability()) {
        setIsScrolled(false)
      }
    })

    resizeObserver.observe(scrollContainer)

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
    }
  }, [effectiveEnableScroll])

  // Merge layout options from context with props (context takes precedence)
  const effectiveFullWidth = fullWidth ?? layoutOptions.fullWidth ?? false
  const effectiveFullHeight = fullHeight ?? layoutOptions.fullHeight ?? false
  const effectiveNoHeaderSpacing =
    noHeaderSpacing ?? layoutOptions.noHeaderSpacing ?? false
  const effectiveNoPadding = noPadding ?? layoutOptions.noPadding ?? false
  const effectiveContainerWidth =
    containerWidth ?? layoutOptions.containerWidth ?? 'default'

  // Helper function to get container width classes
  const getContainerWidthClasses = (
    width: ContainerWidth,
    noPadding: boolean,
    enableScroll: boolean,
  ) => {
    // Always include minimum padding on smaller screens for usability
    // On md+ screens, respect noPadding for full-width layouts
    // --page-px mirrors the active horizontal padding so full-bleed children
    // can cancel it exactly with mx-[calc(var(--page-px,0px)*-1)] regardless
    // of which padding mode is active (see forms builder edit page).
    const paddingClasses = noPadding
      ? 'px-4 sm:px-6 md:px-0 [--page-px:1rem] sm:[--page-px:1.5rem] md:[--page-px:0px]'
      : 'px-4 sm:px-6 md:px-8 lg:px-12 [--page-px:1rem] sm:[--page-px:1.5rem] md:[--page-px:2rem] lg:[--page-px:3rem]'

    switch (width) {
      case 'full':
        // Only add overflow-x-auto if scroll is disabled to avoid conflicts
        return `${paddingClasses} w-full text-left ${!enableScroll ? 'overflow-x-auto' : ''}`
      case 'adaptive':
        // Adaptive based on actual window size (not screen size)
        if (isUltrawide) {
          // Very wide window (2200px+)
          return `max-w-[1800px] ${paddingClasses}`
        } else if (isLargeDesktop) {
          // Large window (1600px+)
          return `max-w-[1600px] ${paddingClasses}`
        } else if (category === 'medium') {
          // Medium window (1200-1599px)
          return `max-w-[1400px] ${paddingClasses}`
        } else {
          // Small window (<1200px)
          return `max-w-[1200px] ${paddingClasses}`
        }
      case 'wide':
        // Static wide width
        return `max-w-[1600px] ${paddingClasses}`
      case 'default':
      default:
        return `max-w-[1400px] ${paddingClasses}`
    }
  }

  // For discussions pages/tabs and workflows, force fullHeight and noPadding
  // Note: isCanvasStyleLayout and effectiveEnableScroll are defined earlier for use in the scroll effect
  const shouldUseFullHeight = effectiveFullHeight || isCanvasStyleLayout
  // For canvas-style layouts, force noPadding to true
  const shouldNoPadding = effectiveNoPadding || isCanvasStyleLayout
  // Only add Page component padding if container doesn't already have padding and it's not a canvas-style page
  const shouldUsePadding = effectiveNoPadding && !isCanvasStyleLayout

  // Determine if we have tabs to adjust spacing accordingly
  const hasTabs = header?.tabs && header.tabs.length > 0

  // For settings pages, always use default width
  // For fullWidth or canvas-style layouts (discussions, workflows), use 'full' containerWidth
  // Otherwise use the specified containerWidth
  const finalContainerWidth = isSettingsPage
    ? 'default'
    : effectiveFullWidth || isCanvasStyleLayout
      ? 'full'
      : effectiveContainerWidth

  const containerClasses = getContainerWidthClasses(
    finalContainerWidth,
    shouldNoPadding,
    effectiveEnableScroll,
  )

  const resolvedHeaderIcon = (() => {
    if (!header) return undefined
    if (header.greeting) return header.icon ?? undefined
    if (header.icon === null) return undefined
    if (header.icon) return header.icon

    const navItems = [...mainNavItems, ...adminNavItems]
    const segments = location.pathname.split('/').filter(Boolean)
    const candidates = [location.pathname]

    // Most app routes are org-scoped (/:orgSlug/...), so check both full and de-scoped paths.
    if (segments.length > 1) {
      candidates.push(`/${segments.slice(1).join('/')}`)
    }

    const matchedItem = navItems
      .filter(item => item.href !== '/today')
      .filter(item =>
        candidates.some(
          candidate =>
            candidate === item.href || candidate.startsWith(`${item.href}/`),
        ),
      )
      .sort((a, b) => b.href.length - a.href.length)[0]

    if (matchedItem?.icon) {
      return matchedItem.icon as ComponentType<{
        className?: string
        weight?: string
      }>
    }

    return undefined
  })()

  const rootHeightClasses = isCanvasStyleLayout
    ? '' // Canvas layouts (threads, discussions, workflows) rely on h-full from parent chain
    : effectiveEnableScroll
      ? 'h-full'
      : 'min-h-full'

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-background dark:bg-[hsl(220_15%_9%)]',
        rootHeightClasses,
      )}
    >
      {/* Header stays fixed outside the scroll container */}
      {header && (
        <PageHeader
          title={header.title}
          titleSuffix={header.titleSuffix}
          description={header.description}
          greeting={header.greeting}
          actions={header.actions}
          tabs={header.tabs}
          condensed={header.condensed || (effectiveEnableScroll && isScrolled)}
          fixed={header.fixed}
          flat={header.flat}
          icon={resolvedHeaderIcon}
          moduleType={header.moduleType}
          entityData={header.entityData}
          onEntityClick={header.onEntityClick}
          onLogoChange={header.onLogoChange}
          backLink={header.backLink}
          onViewChange={header.onViewChange}
          background={header.background}
          hero={header.hero}
          fullWidth={effectiveFullWidth}
          noPadding={effectiveNoPadding}
          containerWidth={finalContainerWidth}
          isLoading={isLoading}
          className={cn(
            effectiveNoHeaderSpacing ? 'mb-0' : '',
            'flex-shrink-0',
            header.className,
          )}
        >
          {header.children}
        </PageHeader>
      )}

      {/* Scrollable content area - full width for scrolling anywhere */}
      <div
        ref={scrollContainerRef}
        className={cn(
          'min-h-0 flex-1',
          effectiveEnableScroll ? 'overflow-y-auto overflow-x-hidden' : '',
        )}
      >
        {/* Sentinel element for intersection observer */}
        {effectiveEnableScroll && (
          <div
            ref={sentinelRef}
            className="pointer-events-none absolute left-0 top-[60px] z-0 h-px w-full"
            aria-hidden="true"
          />
        )}

        {/* Content container with max-width */}
        <div
          className={cn(
            'relative mx-auto w-full min-w-0 transition-all duration-300',
            containerClasses,
            hasTabs
              ? 'mt-1'
              : effectiveNoHeaderSpacing
                ? 'mt-0'
                : header?.fixed
                  ? 'mt-2'
                  : 'mt-3',
            shouldUseFullHeight ? 'flex h-full min-h-0 flex-col' : 'pb-24',
          )}
        >
          {isLoading ? (
            <div className="scrollbar-thin flex-1">
              <div className="space-y-6 px-6 py-6">
                {/* Properties Grid Skeleton - matches OverviewSkeleton */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className="flex animate-pulse flex-col gap-1.5 rounded-lg bg-muted px-3 py-2.5"
                    >
                      <Skeleton className="h-3 w-16 bg-muted-foreground/20" />
                      <Skeleton className="h-5 w-24 bg-muted-foreground/20" />
                    </div>
                  ))}
                </div>

                {/* Content Cards Skeleton */}
                <div className="space-y-2">
                  {[1, 2].map(i => (
                    <div
                      key={i}
                      className="animate-pulse rounded-lg bg-muted p-3"
                    >
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-3/4 bg-muted-foreground/20" />
                        <Skeleton className="h-3 w-1/2 bg-muted-foreground/20" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Page
              className={cn(
                'transition-all duration-300',
                className,
                shouldUseFullHeight && 'flex h-full min-h-0 flex-1 flex-col',
              )}
              noPadding={!shouldUsePadding}
              fullHeight={shouldUseFullHeight}
              noBottomPadding={isCanvasStyleLayout || noBottomPadding}
            >
              {children}
            </Page>
          )}
        </div>
      </div>
    </div>
  )
}
